import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import theme from "../styles/theme";
import { fetchVolleySchemas, isQuizEnabled, trackActivityEvent } from "../api";

type PlayerKey = "Pass" | "Poi" | "Lib" | "R4" | "C1" | "C2";
type SubstituteKey = "Sub_Pass" | "Sub_Poi" | "Sub_Lib" | "Sub_R4" | "Sub_C1" | "Sub_C2";
// Identifiant d'une pastille dans le quiz : soit une clé "sur le terrain" (Pass, R4, ...),
// soit une clé "remplaçante" (Sub_R4, ...) — les schémas utilisent parfois une remplaçante
// pour représenter une 2e joueuse au même poste (ex: 2 R4) réellement placée sur le terrain.
type PuckId = string;
type Pos = { nx: number; ny: number };
type Positions = Partial<Record<PlayerKey, Pos>>;
type Substitutes = Partial<Record<SubstituteKey, Pos>>;
type QuizStepRaw = { label?: string; positions?: Positions; substitutes?: Substitutes; quiz?: boolean };
type SchemaType = "service" | "reception";
type QuizSchema = {
  id: string;
  name: string;
  type: SchemaType;
  playerCount: 4 | 6;
  tactic?: string;
  steps: QuizStepRaw[];
};

const PC: Record<PlayerKey, { bg: string; label: string }> = {
  Pass: { bg: "#ef4444", label: "Passeuse" },
  Poi: { bg: "#8b5cf6", label: "Pointue" },
  Lib: { bg: "#1a1a1a", label: "Libéro" },
  R4: { bg: "#f59e0b", label: "R4" },
  C1: { bg: "#3b82f6", label: "Centrale" },
  C2: { bg: "#ec4899", label: "Centrale" },
};

const P6: PlayerKey[] = ["Pass", "Poi", "Lib", "R4", "C1", "C2"];
const P4: PlayerKey[] = ["Pass", "Poi", "R4", "C1"];
const SUB6: SubstituteKey[] = ["Sub_Pass", "Sub_Poi", "Sub_Lib", "Sub_R4", "Sub_C1", "Sub_C2"];
const SUB4: SubstituteKey[] = ["Sub_Pass", "Sub_Poi", "Sub_R4", "Sub_C1"];

// Rôle affiché derrière une pastille, qu'elle vienne de "positions" (ex: "R4") ou
// d'une remplaçante amenée sur le terrain (ex: "Sub_R4" -> "R4").
function baseRole(p: PuckId): PlayerKey {
  return (p.startsWith("Sub_") ? p.slice(4) : p) as PlayerKey;
}

// Groupe utilisé pour l'appariement au moment de la notation : comme pour R4, les 2 centrales
// (C1/C2, titulaires ou remplaçantes) ne sont pas numérotées à l'écran (juste "C"), donc un
// échange entre les deux ne doit pas être pénalisé — elles forment un seul groupe interchangeable.
function matchGroup(p: PuckId): string {
  const base = baseRole(p);
  return base === "C1" || base === "C2" ? "C" : base;
}

// L'étape 0 ("Réglementaire") est la position au moment de la frappe du service :
// c'est la seule étape où les règles de positionnement s'appliquent.
function getRegStep(steps: QuizStepRaw[]): QuizStepRaw | undefined {
  return steps.find((s) => /r[ée]glementaire/i.test(s.label?.trim() ?? "")) ?? steps[0];
}

// Étape servant de solution au quiz : celle marquée dans le studio (drapeau `quiz`),
// sinon l'« Étape 1 » par défaut — comportement historique, conservé pour tous les
// schémas où rien n'a été configuré.
function getQuizStep(steps: QuizStepRaw[]): QuizStepRaw | undefined {
  return steps.find((s) => s.quiz === true) ?? steps.find((s) => s.label?.trim() === "Étape 1");
}

type Zone = 1 | 2 | 3 | 4 | 5 | 6;

// Déduit le poste (1-6) de chaque pastille à partir des positions cibles de l'étape
// réglementaire : les 3 plus proches du filet forment la ligne avant (4-3-2 de gauche à
// droite), les 3 autres la ligne arrière (5-6-1 de gauche à droite).
function zoneAssignments(targets: Partial<Record<PuckId, Pos>>, players: PuckId[]): Partial<Record<PuckId, Zone>> | null {
  const entries = players
    .map((p) => ({ p, t: targets[p] }))
    .filter((e): e is { p: PuckId; t: Pos } => !!e.t);
  if (entries.length !== 6) return null;
  const sorted = [...entries].sort((a, b) => a.t.ny - b.t.ny);
  const front = sorted.slice(0, 3).sort((a, b) => a.t.nx - b.t.nx);
  const back = sorted.slice(3).sort((a, b) => a.t.nx - b.t.nx);
  const map: Partial<Record<PuckId, Zone>> = {};
  ([4, 3, 2] as Zone[]).forEach((z, i) => (map[front[i].p] = z));
  ([5, 6, 1] as Zone[]).forEach((z, i) => (map[back[i].p] = z));
  return map;
}

// Référence à une pastille dans les messages de faute : son rôle (pour la couleur/le code)
// et le poste (1-6) qu'elle occupe dans la rotation testée.
type PuckRef = { role: PlayerKey; zone: Zone };
type ViolationItem =
  | { kind: "unplaced"; a: PuckRef }
  | { kind: "left" | "front"; a: PuckRef; b: PuckRef };

// Règle FIVB 7.4 (fautes de position) : ordre latéral au sein d'une même ligne,
// et chaque avant devant l'arrière de sa colonne. Rien d'autre n'est contraint.
const OVERLAP_RULES: { a: Zone; b: Zone; kind: "left" | "front" }[] = [
  { a: 4, b: 3, kind: "left" },
  { a: 3, b: 2, kind: "left" },
  { a: 5, b: 6, kind: "left" },
  { a: 6, b: 1, kind: "left" },
  { a: 4, b: 5, kind: "front" },
  { a: 3, b: 6, kind: "front" },
  { a: 2, b: 1, kind: "front" },
];

const MX = 0.11,
  MY = 0.08,
  LW = 1 - MX * 2,
  LH = 1 - MY * 2;

const W = 320;
const COURT_H = 440;
const TRAY_H = 90;
const FULL_H = COURT_H + TRAY_H;
const PUCK_R = 15;
const PERFECT_ZONE = 0.04; // rayon (échelle terrain) toléré à 100%, avant que le score ne devienne dégressif
const TOLERANCE = 0.16; // distance normalisée au-delà de laquelle le score tombe à 0
const SUCCESS_THRESHOLD = 85; // % à partir duquel (et sans faute de position) la position est validée

function getSchemasScope(): string {
  // Scope unique : coachs et joueuses lisent/écrivent le même espace.
  return "global";
}

const ROTATIONS = ["P1", "P2", "P3", "P4", "P5", "P6"] as const;

// "P1"…"P6" extrait du nom d'un schéma (ex: "P4 Reception").
function rotationOf(name: string): string | null {
  return name.match(/P\s*[1-6]/i)?.[0].replace(/\s/g, "").toUpperCase() ?? null;
}

// Meilleurs scores locaux (par rotation), conservés sur l'appareil.
const BEST_SCORES_KEY = "quizBestScores";
type BestEntry = { score: number; clean: boolean };
function readBestScores(): Record<string, BestEntry> {
  try {
    const parsed = JSON.parse(localStorage.getItem(BEST_SCORES_KEY) || "{}");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

// Décalage (px) de la pastille au-dessus du doigt pendant un drag tactile,
// pour qu'elle ne soit pas masquée par le doigt au moment du placement.
const TOUCH_DRAG_OFFSET_PX = 40;

// Confettis de réussite : positions/délais pseudo-aléatoires mais déterministes.
const CONFETTI = Array.from({ length: 30 }, (_, i) => ({
  left: (i * 37 + 11) % 100,
  delay: (((i * 13) % 24) / 24) * 0.7,
  dur: 1.3 + ((i * 7) % 12) / 12,
  color: ["#22c55e", "#f59e0b", "#3b82f6", "#ef4444", "#a855f7", "#eab308"][i % 6],
}));

function scoreForDistance(dist: number): number {
  if (dist <= PERFECT_ZONE) return 100;
  return Math.max(0, Math.round(100 * (1 - (dist - PERFECT_ZONE) / (TOLERANCE - PERFECT_ZONE))));
}

function scoreForPos(placed: Pos | undefined, target: Pos): number {
  if (!placed) return 0;
  return scoreForDistance(Math.hypot(placed.nx - target.nx, placed.ny - target.ny));
}

// Toutes les permutations d'un tableau (utilisé pour de tout petits tableaux, 2 ou 3 éléments).
function permutations<T>(arr: T[]): T[][] {
  if (arr.length <= 1) return [arr];
  const result: T[][] = [];
  arr.forEach((item, i) => {
    const rest = [...arr.slice(0, i), ...arr.slice(i + 1)];
    permutations(rest).forEach((p) => result.push([item, ...p]));
  });
  return result;
}

// Une position est "sur le terrain" si elle tombe dans le rectangle de jeu (hors bandes/banc).
function isOnCourt(pos: Pos): boolean {
  const margin = 0.015;
  return pos.nx >= MX - margin && pos.nx <= MX + LW + margin && pos.ny >= MY - margin && pos.ny <= MY + LH + margin;
}

// Construit la liste des pastilles réellement sur le terrain à une étape donnée, en regardant
// à la fois les positions "titulaires" et les remplaçantes : certains schémas font jouer une
// remplaçante sur le terrain (ex: une 2e R4) plutôt que la titulaire, qui elle reste au banc.
function onCourtRoster(step: QuizStepRaw, playerCount: 4 | 6): { puckId: PuckId; pos: Pos }[] {
  const roster: { puckId: PuckId; pos: Pos }[] = [];
  const baseKeys = playerCount === 6 ? P6 : P4;
  const subKeys = playerCount === 6 ? SUB6 : SUB4;
  baseKeys.forEach((k) => {
    const pos = step.positions?.[k];
    if (pos && isOnCourt(pos)) roster.push({ puckId: k, pos });
  });
  subKeys.forEach((k) => {
    const pos = step.substitutes?.[k];
    if (pos && isOnCourt(pos)) roster.push({ puckId: k, pos });
  });
  return roster;
}

function traySlot(idx: number, total: number): Pos {
  const usable = W - 48;
  const step = total > 1 ? usable / (total - 1) : 0;
  const x = total > 1 ? 24 + idx * step : W / 2;
  return { nx: x / W, ny: 1 + TRAY_H / 2 / COURT_H };
}

function CourtBg() {
  const lx = MX * W,
    ly = MY * COURT_H,
    lw = LW * W,
    lh = LH * COURT_H;
  const netY = ly + lh * 0.12;
  const m3Y = ly + lh * 0.42;
  return (
    <>
      <rect x={0} y={0} width={W} height={COURT_H} rx={8} fill="#0b2e4a" />
      <rect x={lx} y={ly} width={lw} height={lh} fill="#1565a8" />
      <rect x={lx} y={netY} width={lw} height={m3Y - netY} fill="rgba(255,140,30,.30)" stroke="rgba(255,130,0,.6)" strokeWidth={1} />
      <rect x={lx} y={m3Y} width={lw} height={ly + lh - m3Y} fill="rgba(0,200,170,.25)" stroke="rgba(0,200,170,.55)" strokeWidth={1} />
      {[1 / 3, 2 / 3].map((r, i) => (
        <line key={i} x1={lx + r * lw} y1={ly + 1} x2={lx + r * lw} y2={ly + lh - 1} stroke="rgba(255,255,255,.15)" strokeWidth={1} />
      ))}
      <line x1={lx} y1={m3Y} x2={lx + lw} y2={m3Y} stroke="rgba(255,255,255,.55)" strokeWidth={1.5} strokeDasharray="6,4" />
      <line x1={lx} y1={netY} x2={lx + lw} y2={netY} stroke="rgba(255,255,255,.85)" strokeWidth={3} />
      <rect x={lx} y={ly} width={lw} height={lh} fill="none" stroke="rgba(255,255,255,.5)" strokeWidth={2} />
    </>
  );
}

// Pastille inline utilisée dans les textes (rappels, fautes de position) : même code et
// même couleur que les pastilles du terrain, pour que la joueuse fasse le lien visuellement.
function PuckDot({ role }: { role: PlayerKey }) {
  const code = role === "C1" || role === "C2" ? "C" : role;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 24,
        height: 24,
        borderRadius: "50%",
        bgcolor: PC[role].bg,
        color: "#fff",
        fontSize: 9.5,
        fontWeight: 700,
        fontFamily: "monospace",
        verticalAlign: "middle",
        mx: 0.4,
        flex: "none",
        boxShadow: "inset 0 0 0 1.5px rgba(255,255,255,.35)",
      }}
    >
      {code}
    </Box>
  );
}

export default function SchemaQuiz() {
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const scope = useMemo(() => getSchemasScope(), []);

  const [quizAccess, setQuizAccess] = useState<"checking" | "enabled" | "disabled">("checking");
  useEffect(() => {
    let cancelled = false;
    isQuizEnabled()
      .then((enabled) => { if (!cancelled) setQuizAccess(enabled ? "enabled" : "disabled"); })
      .catch(() => { if (!cancelled) setQuizAccess("enabled"); });
    return () => { cancelled = true; };
  }, []);

  const [phase, setPhase] = useState<"setup" | "loading" | "playing" | "result">("setup");
  // Le quiz demande de retrouver l'étape du quiz du schéma (cibles, score, "position exacte") :
  // celle marquée 🎯 dans le studio, sinon l'« Étape 1 » par défaut.
  // L'étape 0 ("Réglementaire") sert uniquement aux conseils sur les positions relatives :
  // c'est elle qui fixe la rotation, donc qui occupe quel poste (1-6).
  // Pour l'instant on ne teste que les phases de RÉCEPTION, à 6 joueuses.
  const typeFilter: SchemaType = "reception";
  const countFilter: 4 | 6 = 6;
  const [rotationFilter, setRotationFilter] = useState<string>("all");
  const [error, setError] = useState<string | null>(null);

  const [pool, setPool] = useState<QuizSchema[]>([]);
  const [current, setCurrent] = useState<QuizSchema | null>(null);
  const [players, setPlayers] = useState<PuckId[]>([]);
  const [targets, setTargets] = useState<Partial<Record<PuckId, Pos>>>({});
  // Positions de l'étape 0 ("Réglementaire") : uniquement pour déduire les postes 1-6
  // des conseils réglementaires, jamais pour le score ni la "position exacte".
  const [regTargets, setRegTargets] = useState<Partial<Record<PuckId, Pos>>>({});
  // Cible effectivement utilisée pour noter chaque pastille après appariement optimal
  // (peut différer de `targets[p]` quand plusieurs pastilles du même poste ont été échangées).
  const [resolvedTargets, setResolvedTargets] = useState<Partial<Record<PuckId, Pos>>>({});
  const [placements, setPlacements] = useState<Partial<Record<PuckId, Pos>>>({});
  const [locked, setLocked] = useState(false);
  const [scores, setScores] = useState<Partial<Record<PuckId, number>>>({});
  const [overallScore, setOverallScore] = useState<number | null>(null);
  // Fautes de position détectées à la validation (null = analyse non applicable,
  // ex: schéma à 4 joueuses ou moins de 6 pastilles sur le terrain).
  const [violations, setViolations] = useState<ViolationItem[] | null>(null);
  // Vue "maintenir pour voir" : tant que le bouton est appuyé, les pastilles glissent
  // sur l'étape du quiz ("step1", la solution) ou sur l'étape 0 ("reg", réglementaire).
  const [holdView, setHoldView] = useState<null | "step1" | "reg">(null);
  const [dragging, setDragging] = useState<PuckId | null>(null);
  const [bestScores, setBestScores] = useState<Record<string, BestEntry>>(readBestScores);
  const [newRecord, setNewRecord] = useState(false);
  // Position réglementaire de chaque pastille APRÈS appariement (si deux pastilles
  // indiscernables ont été échangées au score, leur position réglementaire suit).
  const [resolvedRegTargets, setResolvedRegTargets] = useState<Partial<Record<PuckId, Pos>>>({});

  const svgRef = useRef<SVGSVGElement | null>(null);
  const startedAt = useRef<number>(0);

  const pickRandom = (list: QuizSchema[]) => {
    // Évite de retomber immédiatement sur le schéma qui vient d'être joué
    // (sauf si le pool n'en contient qu'un seul).
    const candidates = current && list.length > 1 ? list.filter((s) => s.id !== current.id) : list;
    const schema = candidates[Math.floor(Math.random() * candidates.length)];
    const quizStep = getQuizStep(schema.steps);
    const roster = quizStep ? onCourtRoster(quizStep, schema.playerCount) : [];
    const ids = roster.map((r) => r.puckId);
    const nextTargets: Partial<Record<PuckId, Pos>> = {};
    roster.forEach((r) => {
      nextTargets[r.puckId] = r.pos;
    });

    const regStep = getRegStep(schema.steps);
    const regRoster = regStep ? onCourtRoster(regStep, schema.playerCount) : [];
    const nextRegTargets: Partial<Record<PuckId, Pos>> = {};
    regRoster.forEach((r) => {
      nextRegTargets[r.puckId] = r.pos;
    });

    const fmt = (r: { puckId: PuckId; pos: Pos }) => `${r.puckId}: nx=${r.pos.nx.toFixed(3)} ny=${r.pos.ny.toFixed(3)}`;
    console.log(`[Quiz] tirage : "${schema.name}" (id ${schema.id})`, {
      étapes: schema.steps.map((s) => s.label ?? "(sans label)"),
      étape_quiz: quizStep?.label ?? "(aucune)",
      cibles_quiz: roster.map(fmt),
      réglementaire: regRoster.map(fmt),
    });

    setCurrent(schema);
    setTargets(nextTargets);
    setRegTargets(nextRegTargets);
    setResolvedTargets({});
    setResolvedRegTargets({});
    setPlayers(ids);
    setPlacements({});
    setScores({});
    setOverallScore(null);
    setViolations(null);
    setHoldView(null);
    setNewRecord(false);
    setLocked(false);
    startedAt.current = Date.now();
    setPhase("playing");
  };

  const loadPool = async () => {
    setPhase("loading");
    setError(null);
    try {
      const raw = await fetchVolleySchemas(scope);
      const mapped: QuizSchema[] = (Array.isArray(raw) ? raw : [])
        .filter((s: any) => s && typeof s === "object")
        .map(
          (s: any): QuizSchema => ({
            id: String(s.id ?? ""),
            name: String(s.name ?? "Schéma"),
            type: s.type === "service" ? "service" : "reception",
            playerCount: Number(s.playerCount) === 4 ? 4 : 6,
            tactic: typeof s.tactic === "string" ? s.tactic : "",
            steps: Array.isArray(s.steps) ? s.steps : [],
          })
        );

      // IMPORTANT — dédoublonnage par id : l'API renvoie la liste triée par updated_at DESC
      // et peut contenir plusieurs versions d'un même schéma (anciennes sauvegardes, doublon
      // global/équipe). On garde la 1re occurrence = la plus récente, comme le studio.
      // Sans ça, le tirage pouvait tomber sur une VIEILLE version du schéma (ex: un P1 où
      // le Lib était encore en case 5 au lieu de 6).
      const seenIds = new Set<string>();
      const dropped: string[] = [];
      const unique = mapped.filter((s) => {
        if (!s.id || seenIds.has(s.id)) {
          dropped.push(`${s.name} (id ${s.id || "vide"})`);
          return false;
        }
        seenIds.add(s.id);
        return true;
      });
      console.log(
        `[Quiz] ${mapped.length} schéma(s) reçu(s) (scope ${scope}) — ${dropped.length} doublon(s)/ancienne(s) version(s) ignoré(s)`,
        dropped
      );

      const list: QuizSchema[] = unique
        .filter((s: QuizSchema) => s.type === typeFilter && s.playerCount === countFilter)
        .filter((s: QuizSchema) => {
          const quizStep = getQuizStep(s.steps);
          if (!quizStep) return false;
          return onCourtRoster(quizStep, s.playerCount).length >= 2;
        });
      // Filtre de rotation choisi sur l'écran de départ (le nom du schéma porte "P1"…"P6").
      const byRotation =
        rotationFilter === "all" ? list : list.filter((s: QuizSchema) => rotationOf(s.name) === rotationFilter);
      console.log(
        `[Quiz] pool final : ${byRotation.length} schéma(s) (rotation ${rotationFilter})`,
        byRotation.map((s) => `${s.name} (id ${s.id})`)
      );

      setPool(byRotation);
      if (byRotation.length === 0) {
        setError(
          rotationFilter === "all"
            ? "Aucun schéma avec une étape de quiz (« Étape 1 » par défaut) définie pour ce filtre."
            : `Aucun schéma disponible pour la rotation ${rotationFilter}.`
        );
        setPhase("setup");
        return;
      }
      pickRandom(byRotation);
    } catch (e: any) {
      setError(e?.message || "Erreur de chargement des schémas.");
      setPhase("setup");
    }
  };

  const rejouer = () => {
    if (pool.length) pickRandom(pool);
  };

  const refaireCettePosition = () => {
    if (!current) return;
    setResolvedTargets({});
    setResolvedRegTargets({});
    setPlacements({});
    setScores({});
    setOverallScore(null);
    setViolations(null);
    setHoldView(null);
    setNewRecord(false);
    setLocked(false);
    startedAt.current = Date.now();
    setPhase("playing");
  };

  const down = (p: PuckId) => (e: React.MouseEvent<SVGGElement> | React.TouchEvent<SVGGElement>) => {
    if (locked) return;
    e.preventDefault();
    e.stopPropagation();
    const svg = svgRef.current;
    if (!svg) return;
    const idx = players.indexOf(p);

    setPlacements((prev) => (prev[p] ? prev : { ...prev, [p]: traySlot(idx, players.length) }));
    setDragging(p);

    const toNorm = (clientX: number, clientY: number) => {
      const r = svg.getBoundingClientRect();
      const nx = (clientX - r.left) / r.width;
      const ny = ((clientY - r.top) / r.height) * (FULL_H / COURT_H);
      return { nx, ny };
    };

    const mv = (ev: MouseEvent | TouchEvent) => {
      const isTouch = "touches" in ev;
      const t = isTouch ? ev.touches[0] : (ev as MouseEvent);
      if (!t) return;
      // Au doigt, la pastille est décalée au-dessus du point de contact
      // pour rester visible pendant le placement.
      const { nx, ny } = toNorm(t.clientX, t.clientY - (isTouch ? TOUCH_DRAG_OFFSET_PX : 0));
      setPlacements((prev) => ({ ...prev, [p]: { nx: Math.max(-0.02, Math.min(1.02, nx)), ny: Math.max(-0.02, Math.min(FULL_H / COURT_H, ny)) } }));
    };

    const up = () => {
      window.removeEventListener("mousemove", mv);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", mv);
      window.removeEventListener("touchend", up);
      setDragging(null);
      setPlacements((prev) => {
        const cur = prev[p];
        if (!cur) return prev;
        if (cur.ny > 1.05) {
          const next = { ...prev };
          delete next[p];
          return next;
        }
        return { ...prev, [p]: { nx: Math.max(0.02, Math.min(0.98, cur.nx)), ny: Math.max(0.02, Math.min(0.98, cur.ny)) } };
      });
    };

    window.addEventListener("mousemove", mv);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", mv, { passive: false });
    window.addEventListener("touchend", up);
  };

  const validate = () => {
    if (!current) return;
    const perPlayer: Partial<Record<PuckId, number>> = {};
    const resolved: Partial<Record<PuckId, Pos>> = {};
    // Après appariement optimal, pastille p a pris la cible qui "appartenait" à owner[p] :
    // on en a besoin pour retrouver le poste réglementaire (1-6) associé à cette cible.
    const resolvedOwner: Partial<Record<PuckId, PuckId>> = {};
    let sum = 0,
      n = 0;

    // Les pastilles ne portent aucun numéro : quand un poste a plusieurs joueuses sur le
    // terrain (ex: 2x R4, ou 2 centrales C1/C2), on ne sait pas laquelle est "la première".
    // On essaie donc tous les appariements possibles entre pastilles placées et positions
    // cibles de ce groupe, et on garde celui qui donne le meilleur score — un simple échange
    // entre deux pastilles indiscernables ne doit pas être compté comme une erreur.
    const groups = new Map<string, PuckId[]>();
    players.forEach((p) => {
      const key = matchGroup(p);
      const list = groups.get(key) || [];
      list.push(p);
      groups.set(key, list);
    });

    groups.forEach((puckIds) => {
      const targetPositions = puckIds.map((p) => targets[p]).filter((t): t is Pos => !!t);
      if (targetPositions.length !== puckIds.length || puckIds.length === 1) {
        puckIds.forEach((p) => {
          const t = targets[p];
          if (!t) return;
          n++;
          resolved[p] = t;
          resolvedOwner[p] = p;
          const s = scoreForPos(placements[p], t);
          perPlayer[p] = s;
          sum += s;
        });
        return;
      }

      let bestPerm: number[] = targetPositions.map((_, i) => i);
      let bestTotal = -1;
      permutations(bestPerm).forEach((perm) => {
        const total = perm.reduce((acc, targetIdx, puckIdx) => acc + scoreForPos(placements[puckIds[puckIdx]], targetPositions[targetIdx]), 0);
        if (total > bestTotal) {
          bestTotal = total;
          bestPerm = perm;
        }
      });

      bestPerm.forEach((targetIdx, puckIdx) => {
        const p = puckIds[puckIdx];
        const t = targetPositions[targetIdx];
        n++;
        resolved[p] = t;
        resolvedOwner[p] = puckIds[targetIdx];
        const s = scoreForPos(placements[p], t);
        perPlayer[p] = s;
        sum += s;
      });
    });

    const overall = n > 0 ? Math.round(sum / n) : 0;

    // Conseils réglementaires : les postes (1-6) sont déduits de l'ÉTAPE 0 ("Réglementaire"),
    // qui fixe la rotation — pas de l'étape du quiz jouée. Les règles sont ensuite
    // vérifiées sur les positions posées par la joueuse (la réception se joue avant la frappe
    // du service, elle doit donc les respecter) — pour dire QUI est mal placée par rapport à QUI.
    let ruleFeedback: ViolationItem[] | null = null;
    const zones = current.playerCount === 6 ? zoneAssignments(regTargets, players) : null;
    if (zones) {
      ruleFeedback = [];
      // Le poste d'une pastille suit la cible qu'elle a prise à l'appariement : si deux
      // centrales indiscernables ont été échangées, leurs postes le sont aussi.
      const zoneOf = (p: PuckId): Zone | undefined => zones[resolvedOwner[p] ?? p];
      const byZone: Partial<Record<Zone, PuckId>> = {};
      players.forEach((p) => {
        const z = zoneOf(p);
        if (z) byZone[z] = p;
      });
      const ref = (p: PuckId, z: Zone): PuckRef => ({ role: baseRole(p), zone: z });
      const EPS = 0.01; // petite tolérance pour ne pas signaler deux pastilles quasi alignées
      players.forEach((p) => {
        const z = zoneOf(p);
        if (z && !placements[p]) ruleFeedback!.push({ kind: "unplaced", a: ref(p, z) });
      });
      OVERLAP_RULES.forEach(({ a, b, kind }) => {
        // Sur un schéma de service, la serveuse (poste 1) ne serait pas soumise aux règles
        // (sans effet tant que le quiz ne propose que des schémas de réception).
        if (current.type === "service" && (a === 1 || b === 1)) return;
        const pa = byZone[a],
          pb = byZone[b];
        if (!pa || !pb) return;
        const la = placements[pa],
          lb = placements[pb];
        if (!la || !lb) return;
        const ok = kind === "left" ? la.nx < lb.nx + EPS : la.ny < lb.ny + EPS;
        if (!ok) ruleFeedback!.push({ kind, a: ref(pa, a), b: ref(pb, b) });
      });
    }

    // Position réglementaire de chaque pastille, en suivant l'appariement du score.
    const regResolved: Partial<Record<PuckId, Pos>> = {};
    players.forEach((p) => {
      const rt = regTargets[resolvedOwner[p] ?? p];
      if (rt) regResolved[p] = rt;
    });

    console.log(`[Quiz] validation : "${current.name}" (id ${current.id})`, {
      appariement: players.map((p) => {
        const t = resolved[p];
        return `${p} → cible de ${resolvedOwner[p] ?? p} (nx=${t?.nx.toFixed(3)} ny=${t?.ny.toFixed(3)}) score=${perPlayer[p]}%`;
      }),
      postes_réglementaires: zones ? players.map((p) => `${p} = poste ${zones[resolvedOwner[p] ?? p] ?? "?"}`) : "analyse n/a",
      placements_joueuse: players.map((p) => {
        const pl = placements[p];
        return pl ? `${p}: nx=${pl.nx.toFixed(3)} ny=${pl.ny.toFixed(3)}` : `${p}: non placée`;
      }),
      fautes: ruleFeedback?.map((v) =>
        v.kind === "unplaced" ? `${v.a.role} (p${v.a.zone}) non placée` : `${v.a.role} (p${v.a.zone}) ${v.kind} ${v.b.role} (p${v.b.zone})`
      ),
    });

    // Meilleur score local (par rotation) — un record "propre" (sans faute) prime
    // sur un record avec faute à score égal.
    const rot = rotationOf(current.name) ?? "Autre";
    const clean = !(ruleFeedback && ruleFeedback.length > 0);
    const prevBest = bestScores[rot];
    const isNewRecord =
      overall > 0 && (!prevBest || overall > prevBest.score || (overall === prevBest.score && clean && !prevBest.clean));
    if (isNewRecord) {
      const nextBest = { ...bestScores, [rot]: { score: overall, clean } };
      setBestScores(nextBest);
      try {
        localStorage.setItem(BEST_SCORES_KEY, JSON.stringify(nextBest));
      } catch {
        /* stockage indisponible : le record ne sera pas conservé */
      }
    }
    setNewRecord(isNewRecord);

    setScores(perPlayer);
    setResolvedTargets(resolved);
    setResolvedRegTargets(regResolved);
    setOverallScore(overall);
    setViolations(ruleFeedback);
    setLocked(true);
    setPhase("result");

    void trackActivityEvent({
      eventType: "quiz_attempt",
      userId: user?.id ?? null,
      username: user?.username ?? null,
      role: user?.role ?? null,
      schemaUid: current.id,
      schemaName: current.name,
      scope,
      success: overall >= SUCCESS_THRESHOLD && !(ruleFeedback && ruleFeedback.length > 0),
      durationMs: Date.now() - startedAt.current,
      metadata: {
        score: overall,
        perPlayer,
        type: current.type,
        playerCount: current.playerCount,
        tactic: current.tactic || null,
        ruleViolations: ruleFeedback ? ruleFeedback.length : null,
      },
    });
  };


  // "P1"..."P6" extrait du nom du schéma (ex: "P4 — Réception"), pour le libellé du bouton.
  const rotationLabel = current ? rotationOf(current.name) : null;

  // L'équipe est "en faute" si au moins une règle de position est violée : le bandeau de
  // score passe en rouge, même si le score de placement est bon par ailleurs.
  const hasFault = (violations?.length ?? 0) > 0;

  if (quizAccess === "checking") {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (quizAccess === "disabled") {
    return (
      <Box sx={{ p: 3, pb: 10 }}>
        <Typography variant="h4" sx={{ mb: 1, color: theme.palette.primary.main, fontWeight: 800 }}>
          Quiz Placement
        </Typography>
        <Alert severity="info" sx={{ mt: 2, maxWidth: 500 }}>
          Le Quiz Placement est temporairement désactivé par un administrateur. Reviens plus tard !
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, pb: 10 }}>
      <Typography
        sx={{
          mb: { xs: 1.5, sm: 3 },
          color: theme.palette.text.secondary,
          fontSize: { xs: 13.5, sm: 16 },
          // Sur mobile, on masque l'intro dès qu'une partie est lancée pour que le
          // terrain et le bouton Valider restent visibles sans scroller.
          display: { xs: phase === "setup" || phase === "loading" ? "block" : "none", sm: "block" },
        }}
      >
        Un schéma est tiré au sort : replace chaque joueuse à sa position de <b>Recep</b>, puis valide pour voir ton score.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, maxWidth: 500 }}>
          {error}
        </Alert>
      )}

      {(phase === "setup" || phase === "loading") && (
        <Box sx={{ mb: { xs: 1.5, sm: 3 } }}>
          {/* Choix de la rotation à travailler (le nom des schémas porte "P1"…"P6"). */}
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center", mb: 1.5 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mr: 0.5 }}>Rotation :</Typography>
            {["all", ...ROTATIONS].map((r) => (
              <Chip
                key={r}
                label={r === "all" ? "Toutes" : r}
                clickable
                size="small"
                color={rotationFilter === r ? "primary" : "default"}
                variant={rotationFilter === r ? "filled" : "outlined"}
                onClick={() => setRotationFilter(r)}
                sx={{ fontWeight: 700 }}
              />
            ))}
          </Box>
          {ROTATIONS.some((r) => bestScores[r]) && (
            <Typography sx={{ fontSize: 13.5, color: theme.palette.text.secondary, mb: 1.5 }}>
              🏆 Tes records :{" "}
              {ROTATIONS.filter((r) => bestScores[r])
                .map((r) => `${r} ${bestScores[r]!.score} %${bestScores[r]!.clean ? " ✅" : ""}`)
                .join(" · ")}
            </Typography>
          )}
          <Button variant="contained" onClick={loadPool} disabled={phase === "loading"}>
            {phase === "loading" ? <CircularProgress size={20} color="inherit" /> : "C'est parti"}
          </Button>
        </Box>
      )}

      {current && (phase === "playing" || phase === "result") && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: { xs: 1.5, sm: 2 } }}>
          {/* Titre-question du schéma + tactique + relance, condensés sur une seule ligne. */}
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", width: { xs: "100%", sm: "auto" } }}>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, sm: 22 }, color: theme.palette.primary.main }}>
              {current.name} ?
            </Typography>
            {current.tactic && (
              <Chip label={current.tactic} variant="outlined" size="small" sx={{ fontWeight: 700, fontSize: 13 }} />
            )}
            <Button variant="outlined" size="small" onClick={loadPool} sx={{ ml: "auto", fontWeight: 700 }}>
              Tester un autre Quiz
            </Button>
            {/* Sortie possible en cours de partie : retour à l'écran de choix de la rotation
                (à l'écran de résultat, le bouton "Changer de rotation" du bas fait déjà ce travail). */}
            {phase === "playing" && (
              <Button variant="contained" color="secondary" size="small" onClick={() => setPhase("setup")} sx={{ fontWeight: 700 }}>
                Tester une autre position
              </Button>
            )}
          </Box>

          {/* Boutons d'action AU-DESSUS du terrain (sticky sur mobile pour rester visibles
              pendant qu'on scrolle vers le banc en bas du terrain). */}
          {phase === "playing" &&
            (players.every((p) => placements[p]) ? (
              <Button
                variant="contained"
                color="success"
                onClick={validate}
                sx={{
                  fontWeight: 700,
                  position: { xs: "sticky", sm: "static" },
                  top: { xs: 8, sm: "auto" },
                  zIndex: 5,
                  alignSelf: { xs: "stretch", sm: "flex-start" },
                  boxShadow: { xs: "0 4px 14px rgba(0,0,0,.25)", sm: 1 },
                }}
              >
                Valider ma position
              </Button>
            ) : (
              // Tant que toutes les pastilles ne sont pas sur le terrain, pas de validation :
              // on affiche la consigne (au même emplacement sticky que le bouton).
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: theme.palette.text.secondary,
                  bgcolor: "background.paper",
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 1.5,
                  px: 2,
                  py: 0.9,
                  textAlign: "center",
                  position: { xs: "sticky", sm: "static" },
                  top: { xs: 8, sm: "auto" },
                  zIndex: 5,
                  alignSelf: { xs: "stretch", sm: "flex-start" },
                }}
              >
                Place les pastilles sur le terrain ({players.filter((p) => placements[p]).length}/{players.length})
              </Typography>
            ))}

          {phase === "result" && (
            <Button
              variant="outlined"
              color="secondary"
              onMouseDown={() => setHoldView("step1")}
              onMouseUp={() => setHoldView(null)}
              onMouseLeave={() => setHoldView(null)}
              onTouchStart={() => setHoldView("step1")}
              onTouchEnd={() => setHoldView(null)}
              onTouchCancel={() => setHoldView(null)}
              onContextMenu={(e) => e.preventDefault()}
              sx={{
                fontWeight: 700,
                position: { xs: "sticky", sm: "static" },
                top: { xs: 8, sm: "auto" },
                zIndex: 5,
                alignSelf: { xs: "stretch", sm: "flex-start" },
                bgcolor: "background.paper",
                "&:hover": { bgcolor: "background.paper" },
                touchAction: "none",
                userSelect: "none",
                WebkitUserSelect: "none",
                WebkitTouchCallout: "none",
              }}
            >
              👁️{" "}
              {holdView === "step1"
                ? `Position exacte${rotationLabel ? ` en ${rotationLabel}` : ""} affichée…`
                : "Maintenir pour voir la position exacte"}
            </Button>
          )}

          {/* Sur mobile le terrain passe en pleine largeur, moins une gouttière à droite :
              le SVG bloque le scroll tactile (touchAction: none), cette bande blanche
              permet de faire défiler la page avec le pouce. */}
          <Box sx={{ position: "relative", width: { xs: "calc(100% - 28px)", sm: W } }}>
            {/* Bandeau de score en surimpression, en haut du terrain :
                rouge = faute de position, vert = validé (>= 85%), bleu = continue tes efforts. */}
            {phase === "result" && overallScore != null && (
              <Box
                sx={{
                  position: "absolute",
                  top: 8,
                  left: 8,
                  right: 8,
                  zIndex: 3,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 2,
                  textAlign: "center",
                  bgcolor: hasFault
                    ? "rgba(220,38,38,.85)"
                    : overallScore >= SUCCESS_THRESHOLD
                    ? "rgba(22,163,74,.85)"
                    : "rgba(37,99,235,.82)",
                  backdropFilter: "blur(2px)",
                  pointerEvents: "none",
                }}
              >
                <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 15.5, lineHeight: 1.35 }}>
                  Score global : {overallScore}%
                  {hasFault
                    ? " — ⚠️ mais l'équipe est en faute de position !"
                    : overallScore >= SUCCESS_THRESHOLD
                    ? " — ✅ Position validée !"
                    : " — 💪 Continue tes efforts !"}
                </Typography>
              </Box>
            )}
            {/* Pluie de confettis quand la position est validée sans faute. */}
            {phase === "result" && overallScore != null && overallScore >= SUCCESS_THRESHOLD && !hasFault && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  overflow: "hidden",
                  pointerEvents: "none",
                  zIndex: 2,
                  "@keyframes quizConfettiFall": {
                    "0%": { top: "-6%", transform: "rotate(0deg)", opacity: 1 },
                    "80%": { opacity: 1 },
                    "100%": { top: "104%", transform: "rotate(520deg)", opacity: 0 },
                  },
                }}
              >
                {CONFETTI.map((c, i) => (
                  <Box
                    key={i}
                    sx={{
                      position: "absolute",
                      top: "-6%",
                      left: `${c.left}%`,
                      width: 7,
                      height: 12,
                      borderRadius: 0.5,
                      bgcolor: c.color,
                      animation: `quizConfettiFall ${c.dur}s ease-in ${c.delay}s both`,
                    }}
                  />
                ))}
              </Box>
            )}
            <svg
              ref={svgRef}
              // Une fois la position validée, le banc ne sert plus : on recadre sur le terrain seul.
              viewBox={`0 0 ${W} ${locked ? COURT_H : FULL_H}`}
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                userSelect: "none",
                touchAction: "none",
                borderRadius: 8,
                boxShadow: "0 2px 10px rgba(0,0,0,.15)",
              }}
            >
              <CourtBg />
              {!locked && (
                <>
                  <rect x={0} y={COURT_H} width={W} height={TRAY_H} fill="#f3f4f6" />
                  <line x1={0} y1={COURT_H} x2={W} y2={COURT_H} stroke="#d1d5db" strokeWidth={1} strokeDasharray="4,3" />
                  <text x={W / 2} y={COURT_H + TRAY_H - 10} textAnchor="middle" fontSize={10} fill="#9ca3af" fontFamily="sans-serif">
                    Banc — glisse les pastilles sur le terrain
                  </text>
                </>
              )}

              {locked &&
                players.map((p) => {
                  const t = resolvedTargets[p];
                  if (!t) return null;
                  return (
                    <circle
                      key={`ghost-${p}`}
                      cx={t.nx * W}
                      cy={t.ny * COURT_H}
                      r={PUCK_R + 4}
                      fill="none"
                      stroke={PC[baseRole(p)].bg}
                      strokeWidth={2}
                      strokeDasharray="3,3"
                      opacity={0.9}
                    />
                  );
                })}
              {locked &&
                holdView !== "reg" &&
                players.map((p) => {
                  const t = resolvedTargets[p];
                  const pl = placements[p];
                  if (!t || !pl) return null;
                  return (
                    <line
                      key={`line-${p}`}
                      x1={pl.nx * W}
                      y1={pl.ny * COURT_H}
                      x2={t.nx * W}
                      y2={t.ny * COURT_H}
                      stroke={PC[baseRole(p)].bg}
                      strokeWidth={1.5}
                      strokeDasharray="4,3"
                      opacity={0.6}
                    />
                  );
                })}

              {/* Pendant l'appui sur un bouton "maintenir pour voir", on laisse une trace
                  estompée de là où la joueuse avait posé chaque pastille. */}
              {locked &&
                holdView !== null &&
                players.map((p) => {
                  const pl = placements[p];
                  if (!pl) return null;
                  return (
                    <circle
                      key={`was-${p}`}
                      cx={pl.nx * W}
                      cy={pl.ny * COURT_H}
                      r={PUCK_R}
                      fill={PC[baseRole(p)].bg}
                      opacity={0.25}
                    />
                  );
                })}

              {/* La pastille en cours de drag est rendue en dernier pour passer au-dessus des autres. */}
              {(dragging ? [...players.filter((q) => q !== dragging), dragging] : players).map((p) => {
                const i = players.indexOf(p);
                const base = baseRole(p);
                // Bouton "maintenir pour voir" appuyé → la pastille glisse sur l'étape du quiz
                // (solution du quiz) ou sur la position réglementaire (étape 0).
                const holdPos =
                  locked && holdView ? (holdView === "step1" ? resolvedTargets[p] : resolvedRegTargets[p]) : undefined;
                const pos = holdPos || placements[p] || traySlot(i, players.length);
                const cx = pos.nx * W,
                  cy = pos.ny * COURT_H;
                const s = scores[p];
                const ringColor = s == null ? "rgba(255,255,255,.55)" : s >= 80 ? "#22c55e" : s >= 50 ? "#f59e0b" : "#ef4444";
                return (
                  <g
                    key={p}
                    transform={`translate(${cx},${cy})${dragging === p ? " scale(1.18)" : ""}`}
                    onMouseDown={down(p)}
                    onTouchStart={down(p)}
                    style={{
                      cursor: locked ? "default" : dragging === p ? "grabbing" : "grab",
                      transition: locked ? "transform .3s ease" : "none",
                    }}
                  >
                    <circle cx={0} cy={0} r={PUCK_R} fill={PC[base].bg} stroke={ringColor} strokeWidth={locked ? 3 : 1.5} />
                    <text x={0} y={0} textAnchor="middle" dominantBaseline="central" fontSize={10} fontWeight={700} fill="#fff" fontFamily="monospace">
                      {base === "C1" || base === "C2" ? "C" : base}
                    </text>
                    {/* Le % mesure l'écart à l'étape du quiz : on le masque pendant la vue réglementaire. */}
                    {locked && s != null && holdView !== "reg" && (
                      <text x={0} y={PUCK_R + 12} textAnchor="middle" fontSize={9} fontWeight={800} fill={ringColor} fontFamily="sans-serif">
                        {s}%
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>
          </Box>

          {phase === "result" && (
            <Box sx={{ width: "100%", maxWidth: 500 }}>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", my: 1.5 }}>
                {newRecord && (
                  <Chip
                    label={`🏆 Nouveau record${rotationLabel ? ` en ${rotationLabel}` : ""} !`}
                    size="small"
                    sx={{ bgcolor: "#fef3c7", color: "#b45309", fontWeight: 800 }}
                  />
                )}
                {players.map((p) => {
                  const base = baseRole(p);
                  const s = scores[p] ?? 0;
                  return (
                    <Chip
                      key={p}
                      label={`${PC[base].label} : ${s}%`}
                      size="small"
                      sx={{ bgcolor: PC[base].bg + "22", color: PC[base].bg, fontWeight: 700 }}
                    />
                  );
                })}
              </Box>

              {violations !== null && (
                <Box
                  sx={{
                    mb: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: violations.length ? "#fdba74" : "#86efac",
                    bgcolor: violations.length ? "#fff7ed" : "#f0fdf4",
                  }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: 14.5, mb: violations.length ? 0.75 : 0 }}>
                    {violations.length
                      ? `⚠️ Position non réglementaire — ${violations.length} faute${violations.length > 1 ? "s" : ""} de placement`
                      : "✅ Position réglementaire : les règles de placement au service sont respectées."}
                  </Typography>
                  {violations.length > 0 && (
                    <Box component="ul" sx={{ m: 0, mb: 0.5, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
                      {violations.map((v, i) => (
                        <Typography component="li" key={i} sx={{ fontSize: 13.5, lineHeight: 1.7 }}>
                          {v.kind === "unplaced" ? (
                            <>
                              <PuckDot role={v.a.role} /> (poste {v.a.zone}) n'a pas été placée sur le terrain.
                            </>
                          ) : (
                            <>
                              <PuckDot role={v.a.role} /> (poste {v.a.zone}) doit être{" "}
                              {v.kind === "left" ? "à gauche de" : "devant"} <PuckDot role={v.b.role} /> (poste {v.b.zone})
                              {v.kind === "front" ? ", plus proche du filet." : "."}
                            </>
                          )}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {Object.keys(resolvedRegTargets).length > 0 && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onMouseDown={() => setHoldView("reg")}
                      onMouseUp={() => setHoldView(null)}
                      onMouseLeave={() => setHoldView(null)}
                      onTouchStart={() => setHoldView("reg")}
                      onTouchEnd={() => setHoldView(null)}
                      onTouchCancel={() => setHoldView(null)}
                      onContextMenu={(e) => e.preventDefault()}
                      sx={{
                        mt: 1,
                        fontWeight: 700,
                        width: { xs: "100%", sm: "auto" },
                        bgcolor: "background.paper",
                        "&:hover": { bgcolor: "background.paper" },
                        touchAction: "none",
                        userSelect: "none",
                        WebkitUserSelect: "none",
                        WebkitTouchCallout: "none",
                      }}
                    >
                      👁️{" "}
                      {holdView === "reg"
                        ? `Position réglementaire${rotationLabel ? ` en ${rotationLabel}` : ""} affichée…`
                        : "Maintenir pour voir la position réglementaire"}
                    </Button>
                  )}
                </Box>
              )}

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                <Button variant="contained" onClick={rejouer}>
                  Rejouer
                </Button>
                <Button variant="outlined" onClick={refaireCettePosition}>
                  Refaire cette position
                </Button>
                <Button variant="contained" color="secondary" onClick={() => setPhase("setup")} sx={{ fontWeight: 700 }}>
                  Tester une autre position
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}

      <Box
        sx={{
          mt: 4,
          p: 2.5,
          maxWidth: 560,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: 16, mb: 1.5 }}>Règles de positionnement au service</Typography>
        <Typography sx={{ fontSize: 14.5, color: theme.palette.text.secondary, mb: 1.5 }}>
          À l'instant où le service est frappé, les relations suivantes doivent être respectées :
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
          {[
            "La joueuse en position 4 doit être à gauche de la joueuse en position 3.",
            "La joueuse en position 3 doit être à gauche de la joueuse en position 2.",
            "La joueuse en position 5 doit être à gauche de la joueuse en position 6.",
            "La joueuse en position 6 doit être à gauche de la joueuse en position 1.",
            "La joueuse en position 4 doit être devant la joueuse en position 5.",
            "La joueuse en position 3 doit être devant la joueuse en position 6.",
            "La joueuse en position 2 doit être devant la joueuse en position 1.",
          ].map((rule, i) => (
            <Typography component="li" key={i} sx={{ fontSize: 14, lineHeight: 1.5 }}>
              {rule}
            </Typography>
          ))}
        </Box>
        <Typography sx={{ fontSize: 13, color: theme.palette.text.secondary, mt: 1.5, lineHeight: 1.5 }}>
          Seules ces relations comptent : rien n'est imposé entre deux joueuses de lignes ET de colonnes différentes (ex: 4 et
          6). La serveuse n'est pas soumise à ces règles au moment où elle sert, et dès que le ballon est frappé chacune peut se
          replacer librement. Dans le quiz, les postes 1-6 des conseils sont déduits de l'étape « Réglementaire » (étape 0) du
          schéma, qui fixe la rotation.
        </Typography>

        <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
          <Typography sx={{ fontWeight: 800, fontSize: 15, mb: 1 }}>Rappel — qui réceptionne ?</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography sx={{ fontSize: 14, lineHeight: 1.9 }}>
              <b>Réceptionnent le service :</b> <PuckDot role="Lib" /> Libéro, <PuckDot role="R4" /> R4 et{" "}
              <PuckDot role="C1" />
              <PuckDot role="C2" /> Centrales (parfois).
            </Typography>
            <Typography sx={{ fontSize: 14, lineHeight: 1.9 }}>
              <b>Ne réceptionnent pas :</b> <PuckDot role="Poi" /> Pointue et <PuckDot role="Pass" /> Passeuse.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
